import React from "react"
import { Dialog, FlatButton, SelectField, MenuItem, TextField, Toggle } from "material-ui"
import strings from "../../../localization/strings"
import autobind from "class-autobind"
import { IOvertimeRule, EOvertimeRuleType, EOTType, IChipSelectItem } from "../../../models"
import * as utils from "../../../helpers/utils"
import { EnumValues } from "enum-values"
import AccountStore from "../../../stores/AccountStore"
import ChipSelect from "../../common/ChipSelect"

interface IProps {
  otType: EOTType
  overtimeRules: IOvertimeRule[]
  editOvertimeRule: IOvertimeRule
  handleAdd(overtimeRule: IOvertimeRule): void
  handleUpdate(overtimeRule: IOvertimeRule): void
  handleClose(): void
  enablePayTypePriority: boolean
}
interface IState {
  overtimeRuleType: EOvertimeRuleType
  errorOvertimeRuleType: string
  overtimeHourThreshold: number
  errorOvertimeHourThreshold: string
  overtimePayTypeId: string
  enableDoubleOvertime: boolean
  doubleOvertimeHourThreshold: number
  errorDoubleOvertimeHourThreshold: string
  doubleOvertimePayTypeId: string
  daysWorkedToTriggerOvertime: number
  errorDaysWorkedToTriggerOvertime: string
	linkedPayTypeIds: string[]
	allDataForChipSelect: IChipSelectItem[]
	linkedPayTypeError: string
}

export default class AddOvertimeRuleDialog extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      overtimeRuleType: props.editOvertimeRule ? props.editOvertimeRule.Type : null,
      errorOvertimeRuleType: "",
      overtimeHourThreshold: props.editOvertimeRule && props.editOvertimeRule.OvertimeHourThreshold ? props.editOvertimeRule.OvertimeHourThreshold : null,
      errorOvertimeHourThreshold: "",
      overtimePayTypeId: props.editOvertimeRule ? props.editOvertimeRule.OvertimePayTypeId : null,
      enableDoubleOvertime: props.editOvertimeRule ? props.editOvertimeRule.EnableDoubleOvertime : false,
      doubleOvertimeHourThreshold: props.editOvertimeRule && props.editOvertimeRule.DoubleOvertimeHourThreshold ? props.editOvertimeRule.DoubleOvertimeHourThreshold : null,
      errorDoubleOvertimeHourThreshold: "",
      doubleOvertimePayTypeId: props.editOvertimeRule ? props.editOvertimeRule.DoubleOvertimePayTypeId : null,
      daysWorkedToTriggerOvertime: props.editOvertimeRule && props.editOvertimeRule.ConsecutiveDaysSettings && props.editOvertimeRule.ConsecutiveDaysSettings.DaysWorkedToTriggerOvertime ? props.editOvertimeRule.ConsecutiveDaysSettings.DaysWorkedToTriggerOvertime : null,
      errorDaysWorkedToTriggerOvertime: '',
			linkedPayTypeIds: props.editOvertimeRule && props.editOvertimeRule.PayTypeIdOvertimePriorityList ? props.editOvertimeRule.PayTypeIdOvertimePriorityList : [],
			allDataForChipSelect: [],
			linkedPayTypeError: "",
    }
    autobind(this)
  }

	componentDidMount() {
		const allDataForChipSelect = []
		AccountStore.getCurrentAccount().PayTypes.filter(item => !item.Deleted).forEach(payType => {
			allDataForChipSelect.push({ Id: payType.Id, Name: payType.Name })
		})
		this.setState({ allDataForChipSelect })
	}

  checkValidate() {
    let isValidate = true
    if (this.state.overtimeRuleType === null) {
      this.setState({ errorOvertimeRuleType: strings.This_field_is_required })
      isValidate = false
    }
    else if (this.props.otType !== EOTType.Bi_Weekly && this.state.overtimeRuleType === EOvertimeRuleType.Bi_Weekly) {
      this.setState({ errorOvertimeRuleType: strings.Cannot_choose_Bi_Weekly })
      isValidate = false
    }
    else {
      this.setState({ errorOvertimeRuleType: "" })
    }

    const maxHourThreshould = this.state.overtimeRuleType === EOvertimeRuleType.Bi_Weekly ?
      336 :
      this.state.overtimeRuleType === EOvertimeRuleType.Weekly ?
        168 :
        24

    if (this.state.enableDoubleOvertime) {
      // OT2 is required
      if (this.state.doubleOvertimeHourThreshold === null) {
        this.setState({ errorDoubleOvertimeHourThreshold: strings.This_field_is_required })
        isValidate = false
      }
      else if (this.state.doubleOvertimeHourThreshold <= 0 || this.state.doubleOvertimeHourThreshold >= maxHourThreshould) {
        this.setState({ errorDoubleOvertimeHourThreshold: strings.formatString(strings.This_field_requires_a_number_between_min_and_max, 0, maxHourThreshould) as string })
        isValidate = false
      }
      else {
        this.setState({ errorDoubleOvertimeHourThreshold: "" })
      }

      // OT1 is optional
      if (this.state.overtimeHourThreshold === null) {
        this.setState({ errorOvertimeHourThreshold: "" })
      }
      else {
        if (this.state.overtimeHourThreshold <= 0 || this.state.overtimeHourThreshold >= maxHourThreshould) {
          this.setState({ errorOvertimeHourThreshold: strings.formatString(strings.This_field_requires_a_number_between_min_and_max, 0, maxHourThreshould) as string })
          isValidate = false
        }
        else {
          this.setState({ errorOvertimeHourThreshold: "" })
        }
      }

      // if OT1 and OT2 exist, OT2 > OT1
      if (
        this.state.overtimeHourThreshold > 0 && this.state.overtimeHourThreshold < maxHourThreshould &&
        this.state.doubleOvertimeHourThreshold > 0 && this.state.doubleOvertimeHourThreshold < maxHourThreshould &&
        Number(this.state.overtimeHourThreshold) >= Number(this.state.doubleOvertimeHourThreshold)
      ) {
        this.setState({ errorDoubleOvertimeHourThreshold: strings.DoubleOvertimeHourThreshold_should_be_greater_than_OvertimeHourThreshold })
        isValidate = false
      }
    }
    else {
      if (this.state.overtimeHourThreshold === null) {
        this.setState({ errorOvertimeHourThreshold: strings.This_field_is_required })
        isValidate = false
      }
      else if (this.state.overtimeHourThreshold <= 0 || this.state.overtimeHourThreshold >= maxHourThreshould) {
        this.setState({ errorOvertimeHourThreshold: strings.formatString(strings.This_field_requires_a_number_between_min_and_max, 0, maxHourThreshould) as string })
        isValidate = false
      }
      else {
        this.setState({ errorOvertimeHourThreshold: "" })
      }
    }

    const maxDaysWorkedToTriggerOvertime = this.props.otType === EOTType.Bi_Weekly ? 14 : 7
    if (this.state.overtimeRuleType === EOvertimeRuleType.ConsecutiveDay) {
      if (!this.state.daysWorkedToTriggerOvertime) {
        this.setState({ errorDaysWorkedToTriggerOvertime: strings.This_field_is_required })
        isValidate = false
      }
      else if (this.state.daysWorkedToTriggerOvertime < 1 || this.state.daysWorkedToTriggerOvertime > maxDaysWorkedToTriggerOvertime) {
        this.setState({ errorDaysWorkedToTriggerOvertime: strings.formatString(strings.This_field_requires_a_number_between_min_and_max, 0, (maxDaysWorkedToTriggerOvertime + 1)) as string })
        isValidate = false
      }
      else {
        this.setState({ errorDaysWorkedToTriggerOvertime: "" })
      }
    }
    return isValidate
  }

  handleSave() {
    if (!this.checkValidate()) return
    const overtimeRule: IOvertimeRule = {
      Id: this.props.editOvertimeRule ? this.props.editOvertimeRule.Id : utils.guid(),
      Type: this.state.overtimeRuleType,
      OvertimeHourThreshold: this.state.overtimeHourThreshold ? this.state.overtimeHourThreshold : null,
      EnableDoubleOvertime: this.state.enableDoubleOvertime,
      DoubleOvertimeHourThreshold: this.state.doubleOvertimeHourThreshold,
      OvertimePayTypeId: this.state.overtimePayTypeId,
      DoubleOvertimePayTypeId: this.state.doubleOvertimePayTypeId,
      ConsecutiveDaysSettings: this.state.overtimeRuleType === EOvertimeRuleType.ConsecutiveDay ?
        { DaysWorkedToTriggerOvertime: this.state.daysWorkedToTriggerOvertime } :
        null,
      PayTypeIdOvertimePriorityList: !this.props.enablePayTypePriority ? null : this.state.linkedPayTypeIds
    }
    this.props.editOvertimeRule ? this.props.handleUpdate(overtimeRule) : this.props.handleAdd(overtimeRule)
  }

	addLinkedPayTypes(obj) {
		if (!obj) return
		const linkedPayTypeIds = this.state.linkedPayTypeIds
		linkedPayTypeIds.push(obj.Id)
		this.setState({ linkedPayTypeIds }, () => this.checkValidate())
	}

	removeLinkedPayTypes(obj) {
				this.removeLinkedPayType(obj.Id)
	}

	removeLinkedPayType(unlinkPayTypeId) {
		const linkedPayTypeIds = this.state.linkedPayTypeIds
		const indexOf = linkedPayTypeIds.indexOf(unlinkPayTypeId)
		if (indexOf > -1) {
			linkedPayTypeIds.splice(indexOf, 1)
			this.setState({ linkedPayTypeIds }, () => this.checkValidate())
		}
	}

  render() {

		const linkedDataForChipSelect = []
		this.state.linkedPayTypeIds.forEach((payTypeId) => {
			const linkedPayType = AccountStore.getCurrentAccount().PayTypes.filter(item => !item.Deleted).find(payType => payType.Id === payTypeId)
			linkedDataForChipSelect.push({
				Id: linkedPayType.Id,
				Name: linkedPayType.Name
			})
		})
        
    return (
      <Dialog
        open
        title={this.props.editOvertimeRule ? strings.Edit_Overtime_Rule : strings.Add_Overtime_Rule}
        titleStyle={{ paddingLeft: 35, paddingBottom: 10 }}
        actions={[
          <FlatButton
            data-intercom-target={"Btn-CloseAddOvertimeRuleDialog"}
            key="cancel"
            label={strings.CANCEL}
            secondary
            onTouchTap={this.props.handleClose}
          />,
          <FlatButton
            data-intercom-target={"Btn-SaveOvertimeRule"}
            key="save"
            label={strings.SAVE}
            primary
            onTouchTap={this.handleSave}
          />
        ]}
      >
        <hr />
        <div style={{ paddingLeft: 15, paddingRight: 15 }}>
          <SelectField
            data-intercom-target={"AddOvertimeRuleDialog-Select-OvertimeRuleType"}
            floatingLabelText={strings.Overtime_Rule_Type}
            disabled={this.props.editOvertimeRule !== null}
            value={this.state.overtimeRuleType}
            onChange={(event, key, overtimeRuleType) => this.setState({ overtimeRuleType }, () => this.checkValidate())}
            errorText={this.state.errorOvertimeRuleType}
            errorStyle={{ position: "absolute", top: 70 }}
          >
            {EnumValues.getValues(EOvertimeRuleType)
              .filter(type => this.props.otType === EOTType.Bi_Weekly ? true : type !== EOvertimeRuleType.Bi_Weekly)
              .filter(type => this.props.editOvertimeRule && this.props.editOvertimeRule.Type === type ? true : this.props.overtimeRules.find(rule => rule.Type === type) === undefined)
              .map(type =>
                <MenuItem key={type} value={type} primaryText={strings[EnumValues.getNameFromValue(EOvertimeRuleType, type)]} />
              )
            }
          </SelectField>
          <div style={{ display: "flex" }}>
            <div style={{ flex: 1 }}>
              <TextField
                data-intercom-target={"AddOvertimeRuleDialog-Select-OvertimeHourThreshold"}
                floatingLabelText={strings.Overtime_Hour_Threshold}
                value={this.state.overtimeHourThreshold === null ? "" : this.state.overtimeHourThreshold}
                onChange={(e, value) =>
                  value === "" ?
                    this.setState({ overtimeHourThreshold: null }, () => this.checkValidate()) :
                    !isNaN(value) &&
                    this.setState({ overtimeHourThreshold: value }, () => this.checkValidate())
                }
                errorText={this.state.errorOvertimeHourThreshold}
                errorStyle={{ position: "absolute", bottom: -12 }}
              />
            </div>
          </div>
          {!AccountStore.getHideOT2() &&
            <Toggle
              data-intercom-target={"AddOvertimeRuleDialog-Toggle-EnableDoubleOvertime"}
              label={strings.Enable_Double_Overtime}
              toggled={this.state.enableDoubleOvertime}
              onToggle={(e, value) => this.setState({ enableDoubleOvertime: value, doubleOvertimeHourThreshold: value ? this.state.doubleOvertimeHourThreshold : null }, () => this.checkValidate())}
              style={{ width: 250, marginTop: 25 }}
            />
          }
          {this.state.enableDoubleOvertime &&
            <div style={{ display: "flex" }}>
              <div style={{ flex: 1 }}>
                <TextField
                  data-intercom-target={"AddOvertimeRuleDialog-Entry-DoubleOvertimeHourThreshold"}
                  floatingLabelText={strings.Double_Overtime_Hour_Threshold}
                  value={this.state.doubleOvertimeHourThreshold === null ? "" : this.state.doubleOvertimeHourThreshold}
                  onChange={(e, value) =>
                    value === "" ?
                      this.setState({ doubleOvertimeHourThreshold: null }, () => this.checkValidate()) :
                      !isNaN(value) &&
                      this.setState({ doubleOvertimeHourThreshold: value }, () => this.checkValidate())
                  }
                  errorText={this.state.errorDoubleOvertimeHourThreshold}
                  errorStyle={{ position: "absolute", bottom: -12 }}
                />
              </div>
              {/* <div style={{ flex: 1 }}>
                <SelectField
                  floatingLabelText={strings.Double_Overtime_PayType}
                  value={this.state.doubleOvertimePayTypeId}
                  onChange={(event, key, doubleOvertimePayTypeId) => this.setState({ doubleOvertimePayTypeId })}
                >
                  {AccountStore.getCurrentAccount().PayTypes.filter(item => !item.Deleted).map(payType =>
                    <MenuItem key={payType.Id} value={payType.Id} primaryText={payType.Name} />
                  )}
                </SelectField>
              </div> */}
            </div>
          }
          {this.state.overtimeRuleType === EOvertimeRuleType.ConsecutiveDay &&
            <TextField
              data-intercom-target={"AddOvertimeRuleDialog-Entry-DaysWorkedToTriggerOvertime"}
              floatingLabelText={strings.Days_Worked_To_Trigger_Overtime}
              value={this.state.daysWorkedToTriggerOvertime === null ? "" : this.state.daysWorkedToTriggerOvertime}
              onChange={(e, value) =>
                utils.validateInteger(value) &&
                this.setState({ daysWorkedToTriggerOvertime: value }, () =>
                  this.checkValidate()
                )
              }
              errorText={this.state.errorDaysWorkedToTriggerOvertime}
              errorStyle={{ position: "absolute", bottom: -12 }}
            />
          }
          {this.props.enablePayTypePriority && this.state.overtimeRuleType === EOvertimeRuleType.Weekly &&
            <div style={{ flex: 1 }}>
              <ChipSelect
                title={strings.Overtime_PayType_Priority}
                titleStyle={{ color: "rgba(0, 0, 0, 0.3)", fontSize: 12 }}
                allData={this.state.allDataForChipSelect}
                linkedData={linkedDataForChipSelect}
                chipStyle={{ marginTop: 10, marginRight: 5, height: 35 }}
                addButtonStyle={{ height: 33, marginTop: 2, display: "inline-block" }}
                labelTextForSelect={strings.Overtime_PayType_Priority}
                handleAddOne={this.addLinkedPayTypes}
                handleDeleteOne={this.removeLinkedPayTypes}
                errorText={this.state.linkedPayTypeError}
              />
            </div>           
          }
        </div>
      </Dialog >
    )
  }
}